import React, { useEffect, useRef, useState } from 'react';
import { FormControl, TextField } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';

import { isPeselValid } from 'app/services/pesel.service';
import { UtilService } from 'app/services/util.service';
import { ControlContainer } from 'app/components/ControlContainer';
import { PeselControlProps } from './PeselControl.interfaces';
import { DoneAdornment } from 'app/components/DoneAdornment';
import { clearAnonymization } from 'app/shared/helpers/clearAnonymization';
import { FieldsEnum, FormsEnum } from 'app/shared/enums';
import { useStyles } from './PeselControl.styles';
import { selectShouldCheckIfConsentResetRequired } from 'app/store/data/consents';
import { getQuotationId, selectQuotation } from 'app/store/data/quotation';
import {
  checkAndResetMarketingConsentIfRequired,
  getConsentResetPersonFlag,
  getConsentResetPersonGroup,
  shouldCheckConsentResetForTrackedPerson,
} from 'app/shared/helpers/checkAndResetMarketingConsent';

// Keep PESEL uniqueness checks lazy-loaded: this path runs on first screen and
// eager imports here noticeably increase the initial bundle/cold start.
let validateIsUniquePeselPromise: Promise<
  typeof import('app/validators/validateIsUniquePesel')
> | null = null;
let revalidatePeselsUniquenessPromise: Promise<
  typeof import('app/services/pesel.service/revalidatePeselsUniqueness')
> | null = null;

const validateUniquePesel = (
  value: string,
  questionKey: FieldsEnum,
  formName: FormsEnum
) => {
  if (!validateIsUniquePeselPromise) {
    validateIsUniquePeselPromise = import(
      'app/validators/validateIsUniquePesel'
    );
  }

  void validateIsUniquePeselPromise.then(
    ({ default: validateIsUniquePesel }) => {
      validateIsUniquePesel({
        value,
        questionKey,
        formName,
      });
    }
  );
};

const revalidateAllPesels = (formName: FormsEnum) => {
  if (!revalidatePeselsUniquenessPromise) {
    revalidatePeselsUniquenessPromise = import(
      'app/services/pesel.service/revalidatePeselsUniqueness'
    );
  }

  void revalidatePeselsUniquenessPromise.then(
    ({ revalidatePeselsUniqueness }) => {
      revalidatePeselsUniqueness(formName);
    }
  );
};

function containsNonNumeric(value: string): boolean {
  return /[^*\d]/.test(value);
}

export default function PeselControl({
  input,
  formName,
  input: { value },
  questionKey,
  meta: { touched, valid },
  meta,
  subLabel,
  tooltip,
  disabled,
  helperText,
  label,
  minAge,
  onValueChange,
  onControlBlur,
  onClick,
  width,
  isQuestionValid,
}: PeselControlProps) {
  const dispatch = useDispatch();
  const shouldCheckIfConsentResetRequired = useSelector(
    selectShouldCheckIfConsentResetRequired
  );
  const quotationId = useSelector(getQuotationId);
  const { insurantSelection } = useSelector(selectQuotation);
  const [focused, setFocused] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');
  const localValueRef = useRef(localValue);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const classes = useStyles();

  const shouldShowDoneAdornment =
    isQuestionValid &&
    !focused &&
    isPeselValid(localValue, minAge) &&
    UtilService.hasValue(localValue);

  useEffect(() => {
    localValueRef.current = localValue;
  }, [localValue]);

  useEffect(() => {
    if (!focused) {
      setLocalValue(value || '');
    }
  }, [value, focused]);

  useEffect(() => {
    const form = inputRef.current?.form;
    if (!form) {
      return;
    }

    const syncBeforeSubmit = () => {
      input.onChange(localValueRef.current);
    };

    form.addEventListener('submit', syncBeforeSubmit);

    return () => {
      form.removeEventListener('submit', syncBeforeSubmit);
    };
  }, [input]);

  useEffect(() => {
    if (!formName) {
      return;
    }

    if (
      [
        FormsEnum.CheckoutForm,
        FormsEnum.CalculationForm,
        FormsEnum.CompleteDataForm,
      ].includes(formName)
    ) {
      revalidateAllPesels(formName);
    } else {
      validateUniquePesel(value, questionKey as FieldsEnum, formName);
    }
    // eslint-disable-next-line
  }, [input]);

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    handleFocus(e.target.value);
    setFocused(true);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (containsNonNumeric(value)) {
      return;
    }

    setLocalValue(value);

    if (value.length === 11 || value.length === 0) {
      input.onChange(value);
    }
  };

  const handleFocus = (value: string) => {
    const clearedValue = clearAnonymization(value);

    if (clearedValue !== value) {
      setLocalValue(clearedValue);
      input.onChange(clearedValue);
      if (onValueChange) {
        onValueChange(clearedValue, false, false);
      }
    }
  };

  const handleBlur = async () => {
    setFocused(false);
    input.onChange(localValue);
    input.onBlur(localValue);

    if (onValueChange) {
      setTimeout(() => onValueChange(localValue, true, false, false), 0);
    }

    const isValid = isPeselValid(localValue, minAge);
    const trackedPerson =
      questionKey === FieldsEnum.OwnerPesel ||
      questionKey === FieldsEnum.CheckoutOwnerPesel
        ? 'owner'
        : questionKey === FieldsEnum.Coowners1Pesel ||
          questionKey === FieldsEnum.CheckoutCoowners1Pesel
        ? 'coowner1'
        : questionKey === FieldsEnum.Coowners2Pesel ||
          questionKey === FieldsEnum.CheckoutCoowners2Pesel
        ? 'coowner2'
        : questionKey === FieldsEnum.CheckoutInsurantPesel
        ? 'different'
        : null;
    const shouldTrackThisPerson =
      trackedPerson &&
      shouldCheckConsentResetForTrackedPerson(trackedPerson, insurantSelection);
    const personGroup = getConsentResetPersonGroup(insurantSelection);
    const shouldCheck = getConsentResetPersonFlag(
      shouldCheckIfConsentResetRequired,
      personGroup,
      'peselNumber'
    );

    if (isValid && shouldTrackThisPerson && shouldCheck) {
      await checkAndResetMarketingConsentIfRequired({
        quotationId,
        dispatch,
        shouldCheck,
        requestData: {
          [personGroup]: {
            peselNumber: value,
          },
        },
        fieldToResetPath: {
          type: 'person',
          personGroup,
          field: 'peselNumber',
        },
        formName,
        consentFieldKey:
          formName === FormsEnum.CheckoutForm
            ? FieldsEnum.InsuranceMarketingConsent
            : FieldsEnum.InsuranceMarketingEmailConsent,
      });
    }

    if (onControlBlur) {
      const shouldPatch = isValid || !localValue;
      onControlBlur(shouldPatch, false, false, false, localValue);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <ControlContainer
      inputId={input.name}
      tooltip={tooltip}
      meta={meta}
      subLabel={subLabel}
      helperText={helperText}
      floating
      floated={focused || UtilService.hasValue(localValue)}
      focused={focused}
      width={width}
    >
      <FormControl fullWidth>
        <TextField
          {...input}
          value={localValue}
          inputRef={inputRef}
          id={input.name}
          inputProps={{
            maxLength: 11,
          }}
          onChange={handleChange}
          onBlur={handleBlur}
          onClick={handleClick}
          onFocus={onFocus}
          label={label}
          InputLabelProps={{
            shrink: UtilService.hasValue(localValue) || focused,
          }}
          InputProps={{
            startAdornment: shouldShowDoneAdornment && <DoneAdornment />,
            classes: {
              root: classes.inputRoot,
            },
          }}
          error={touched && !valid}
          disabled={disabled}
          variant={'outlined'}
          margin={'dense'}
          type="tel"
        />
      </FormControl>
    </ControlContainer>
  );
}
